import { useLocation } from "wouter"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import type { CockpitPillar } from "@shared/dashboard"

// The strategy pillars — included for every customer (addon gate removed 2026-09).
// Format chips fill in once the Variance Engine writes formats; until then the row is hidden.

interface PillarsTileProps {
  pillars: CockpitPillar[]
  /** Hotel-Edition: the strategy is the "LinkedIn-Strategie" (PLAN §5.1 item 4, Mike A4). */
  hotel?: boolean
  /** Hotel with the Themenkatalog live: link to it next to the strategy (Mike A6). */
  themenkatalog?: boolean
}

export function PillarsTile({ pillars, hotel = false, themenkatalog = false }: PillarsTileProps) {
  const [, setLocation] = useLocation()

  return (
    <Card className="p-4 sm:p-5" data-testid="cockpit-pillars">
      <div className="mb-3 flex items-baseline gap-3">
        <h2 className="font-playfair text-[19px] font-bold">
          {pillars.length === 3 ? "Ihre drei Säulen" : "Ihre Säulen"}
        </h2>
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{hotel ? "LinkedIn-Strategie" : "Marketingstrategie"}</span>
        {themenkatalog && (
          <button
            type="button"
            onClick={() => setLocation("/themenkatalog")}
            className="ml-auto text-[12.5px] font-semibold text-primary hover:underline"
            data-testid="link-open-themenkatalog"
          >
            Themenkatalog öffnen
          </button>
        )}
        {pillars.length > 0 && (
          <button
            type="button"
            onClick={() => setLocation("/marketingstrategie")}
            className={`${themenkatalog ? "" : "ml-auto "}text-[12.5px] font-semibold text-primary hover:underline`}
            data-testid="link-open-strategy"
          >
            Strategie öffnen
          </button>
        )}
      </div>
      {themenkatalog && (
        // Which catalogue steers what (Mike 2026-10-10, Klicktest Befund 3): Instagram = Themenkatalog,
        // LinkedIn and free posts = strategy (see the strategy task in WeeklyTaskCard).
        <p className="-mt-1 mb-3 text-xs text-muted-foreground" data-testid="themenkatalog-hint">
          Der Themenkatalog steuert Ihre Instagram-Wochenbeiträge.
        </p>
      )}

      {pillars.length === 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-muted/50 px-4 py-4" data-testid="pillars-empty">
          <p className="flex-1 text-sm text-muted-foreground">
            Ihre Strategie ist noch nicht erstellt. Drei Fragen genügen, den Rest übernimmt Lina.
          </p>
          <Button variant="outline" onClick={() => setLocation("/marketingstrategie")} data-testid="button-create-strategy">
            Strategie erstellen
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {pillars.map((p) => (
            <div key={p.index} className="rounded-lg border-t-[3px] border-primary bg-muted/50 px-3.5 pb-3 pt-3.5" data-testid={`pillar-${p.index}`}>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary">
                Säule {p.index}{p.typeLabel ? `, ${p.typeLabel}` : ""}
              </p>
              <p className="font-playfair text-[15px] font-bold leading-tight">{p.name}</p>
              {p.description && <p className="mt-1.5 text-xs text-muted-foreground">{p.description}</p>}
              {p.formats.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1">
                  {p.formats.map((f) => (
                    <span
                      key={f.id}
                      className={`rounded-full border px-1.5 py-0.5 text-[10px] ${
                        f.recent || f.planned
                          ? "border-transparent bg-primary/10 font-semibold text-primary"
                          : "border-border bg-card text-muted-foreground"
                      }`}
                    >
                      {f.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
